import { AuthClient } from '@cheqroom/api';
import { DateTime } from '@cheqroom/date-time';
import parseJwt from 'jwt-decode';

import { fetchUser } from '../../api/authentication';
import { createAuthStorage, webAuthStorage } from './auth-storage';
import {
	AUTH_URL,
	DEFAULT_API_URL,
	MIN_REFRESH_TIME_IN_MINUTES,
	RESERVED_PATH_SEGMENTS,
	STORAGE_KEYS,
} from './constants';
import { mapUserApiResponseToUser } from './map-user-api-response';
import { resolveUserFromKnockout } from './resolve-user-knockout';
import type { AnonymousUser, AuthenticationOptions, JwtPayload, User } from './types';

export class Authentication {
	private readonly options: Required<Pick<AuthenticationOptions, 'userSource'>> & {
		redirectToLogin?: () => void;
		apiBaseUrl: string;
	};
	private userCache: User | null = null;
	private authClient: AuthClient;

	constructor(options: AuthenticationOptions = {}) {
		this.options = {
			redirectToLogin: options.redirectToLogin,
			userSource: options.userSource ?? 'knockout',
			apiBaseUrl: options.apiBaseUrl ?? DEFAULT_API_URL ?? '',
		};
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				this.storeLastActiveWorkspace(this.getWorkspaceId());
			}
		});

		this.authClient = new AuthClient(
			createAuthStorage(webAuthStorage, this.getWorkspaceId, this.getWorkspaceIdFromAccessToken, {
				onTokensStored: this.storeLastActiveWorkspace,
			})
		);
	}

	authenticate = async () => {
		const currentLocation = new URL(window.location.toString());
		const hashParams = new URLSearchParams(currentLocation.hash.slice(1));
		const accessToken = hashParams.get('access_token') || currentLocation.searchParams.get('access_token');
		const sessionToken = hashParams.get('session_token') || currentLocation.searchParams.get('session_token');
		const redirectUri = currentLocation.searchParams.get('redirect_uri');

		currentLocation.hash = '';
		currentLocation.searchParams.delete('access_token');
		currentLocation.searchParams.delete('session_token');
		currentLocation.searchParams.delete('redirect_uri');

		if (redirectUri) {
			const [path, searchParams] = redirectUri.split('?');
			currentLocation.pathname = path;
			if (searchParams) currentLocation.search = searchParams;
		}

		window.history.replaceState({}, '', currentLocation);

		if (accessToken && sessionToken) {
			this.storeTokens(accessToken, sessionToken);
			return [accessToken, sessionToken] as const;
		}
		this.storeLastActiveWorkspace(this.getWorkspaceId());

		// Call getAccessToken to refresh the token if it's about to expire
		await this.getAccessToken();
		const tokens = webAuthStorage.getTokens(this.getWorkspaceId());
		return [tokens?.accessToken as string, tokens?.sessionToken as string] as const;
	};

	decode = (token: string): JwtPayload => {
		return parseJwt<JwtPayload>(token);
	};

	private isTokenAboutToExpire = (expiry: number, minRefreshTime: number): boolean => {
		const timeDifferenceMS = new Date(expiry * 1000).getTime() - new Date().getTime();
		const timeDifferenceMinutes = Math.round(timeDifferenceMS / 60000);
		return timeDifferenceMinutes < minRefreshTime;
	};

	private isTokenExpired = (expire: number): boolean => {
		return DateTime.now().isAfter(DateTime.fromDate(new Date(expire * 1000)));
	};

	// Hooks that live inside web repo use this method to get the access token
	getAccessToken = async (): Promise<string> => {
		const accessToken = await this.authClient.getAccessToken();
		if (!accessToken) {
			throw new Error('No access token');
		}

		const parsedToken = this.decode(accessToken);
		if (this.isTokenAboutToExpire(parsedToken.exp, MIN_REFRESH_TIME_IN_MINUTES)) {
			try {
				const newTokens = await this.authClient.refreshToken();
				this.storeTokens(newTokens.accessToken, newTokens.sessionToken);
				return newTokens.accessToken;
			} catch (error) {
				if (!this.isTokenExpired(parsedToken.exp)) return accessToken;
				this.redirectToLogin();
				throw error;
			}
		}

		return accessToken;
	};

	getAuthClient = (): AuthClient => {
		return this.authClient;
	};

	private hasScope = (scopeName: string, defaultValue = false): boolean => {
		try {
			return this.decode(webAuthStorage.getTokens(this.getWorkspaceId())?.accessToken ?? '').scope.includes(
				scopeName
			);
		} catch {
			return defaultValue;
		}
	};

	hasWorkspaceAccess = (): boolean => {
		return this.hasScope('workspace', true);
	};

	hasBookingPortalAccess = (): boolean => {
		return this.hasScope('booking_portal');
	};

	logout = (): void => {
		localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVE_WORKSPACE);
		webAuthStorage.clearTokens();

		this.userCache = null;
		if (this.options.redirectToLogin) this.options.redirectToLogin();
	};

	redirectToLogin = (): void => {
		if (this.options.redirectToLogin) {
			this.options.redirectToLogin();
			return;
		}
		const workspaceId = this.getWorkspaceId();
		const hasWorkspaceIdInUrl = this.getWorkspaceIdFromPath();
		let redirectUrlWithoutWorkspaceId = window.location.pathname;
		if (hasWorkspaceIdInUrl) {
			redirectUrlWithoutWorkspaceId = `/${window.location.pathname.split('/').slice(2).join('/')}`;
		}
		if (window.location.search) {
			redirectUrlWithoutWorkspaceId = `${redirectUrlWithoutWorkspaceId}${window.location.search}`;
		}
		const path = workspaceId ? `/${workspaceId}/login` : '/workspaces';
		const loginUrl = new URL(`${AUTH_URL}${path}`);
		loginUrl.searchParams.set('redirect_uri', redirectUrlWithoutWorkspaceId);
		window.location.replace(loginUrl);
	};

	getUser({ anonymous }: { anonymous: true }): AnonymousUser;
	getUser({ anonymous }: { anonymous: false }): User;
	getUser(): User;
	getUser({ anonymous = false } = {}): User | AnonymousUser {
		const workspaceId = this.getWorkspaceId();
		let organisationId: string | undefined;
		try {
			const accessToken = webAuthStorage.getTokens(workspaceId)?.accessToken;
			if (accessToken) organisationId = this.decode(accessToken).organisation;
		} catch {
			// noop
		}

		if (anonymous) {
			return { organisationId, workspace: { id: workspaceId } };
		}

		if (this.options.userSource === 'api') {
			throw new Error(
				'getUser() is synchronous and only supported when userSource is "knockout". Use getUserAsync() when userSource is "api".'
			);
		}

		return resolveUserFromKnockout(organisationId ?? '');
	}

	getUserAsync = async (opts?: { anonymous?: boolean }): Promise<User | AnonymousUser | null> => {
		const anonymous = opts?.anonymous ?? false;
		const workspaceId = this.getWorkspaceId();
		let organisationId: string | undefined;
		try {
			const accessToken = webAuthStorage.getTokens(workspaceId)?.accessToken;
			if (accessToken) organisationId = this.decode(accessToken).organisation;
		} catch {
			// noop
		}

		if (anonymous) return { organisationId, workspace: { id: workspaceId } };

		if (this.options.userSource === 'knockout') {
			return this.getUser({ anonymous: false });
		}

		if (this.userCache) return this.userCache;

		if (!this.options.apiBaseUrl?.trim()) {
			if (process.env.NODE_ENV === 'development') {
				// eslint-disable-next-line no-console
				console.error('apiBaseUrl is required when userSource is "api"');
			}
			return null;
		}

		try {
			const accessToken = await this.getAccessToken();
			const payload = this.decode(accessToken);
			const apiUser = await fetchUser(payload.sub);
			const user = mapUserApiResponseToUser(apiUser);
			this.userCache = user;
			return user;
		} catch (error) {
			if (process.env.NODE_ENV === 'development') {
				// eslint-disable-next-line no-console
				console.error('Failed to get user:', error);
			}
			return null;
		}
	};

	getWorkspaceId = (): string => {
		const fromPath = this.getWorkspaceIdFromPath();
		if (fromPath) return fromPath;
		const fromLastActive = this.getWorkspaceIdFromLastActive();
		if (fromLastActive) return fromLastActive;

		return webAuthStorage.listWorkspaces()?.[0] ?? '';
	};

	getWorkspaceIdFromPath = (): string | null => {
		const firstPathParameter = window.location.pathname.split('/')[1];
		if (firstPathParameter != null && RESERVED_PATH_SEGMENTS.includes(firstPathParameter)) return null;
		return firstPathParameter ?? null;
	};

	private getWorkspaceIdFromLastActive = (): string | null => {
		return localStorage.getItem(STORAGE_KEYS.LAST_ACTIVE_WORKSPACE);
	};

	private getWorkspaceIdFromAccessToken = (accessToken: string): string => {
		return this.decode(accessToken).workspace;
	};

	storeTokens = (accessToken: string, sessionToken: string) => {
		const workspaceId = this.decode(accessToken).workspace;
		void webAuthStorage.setTokens(workspaceId, { accessToken, sessionToken });
		this.storeLastActiveWorkspace(workspaceId);
	};

	private storeLastActiveWorkspace = (workspaceId: string) => {
		if (workspaceId) {
			localStorage.setItem(STORAGE_KEYS.LAST_ACTIVE_WORKSPACE, workspaceId);
		}
	};
}
